import { useQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { fetchEbuyOpportunityUnreadCount } from '@/hooks/ebuy/fetchers'
import { ebuyQueryKeys } from '@/hooks/ebuy/types'

import type { UUID } from '@/types/Id'

interface UseEbuyUnreadCountProps {
  orgId: UUID | ''
  /**
   * Gate the poll off when the caller can't surface the result — e.g. an org
   * without the eBuy entitlement. Defaults to on. Prevents a perpetual 30s
   * background poll whose count is never rendered.
   */
  enabled?: boolean
}

export const useEbuyUnreadCount = ({
  orgId,
  enabled = true,
}: UseEbuyUnreadCountProps) => {
  // Plain useAuthInfo, NOT useAuthInfoWithThrow: this hook runs inside the
  // sidebar shell, which gets statically prerendered through public pages
  // (e.g. the /recipients/usaspending/[id] fallback). A throwing auth hook
  // there crashes `next build`. Gate the query on the token instead — same
  // pattern as useHasMonitorUnread.
  const { accessToken } = useAuthInfo()

  return useQuery<number>({
    // Must match the key that read-state mutations optimistically update so
    // the badge reacts immediately when opportunities are marked read/unread.
    queryKey: ebuyQueryKeys.opportunityUnreadCount(orgId),
    queryFn: () => fetchEbuyOpportunityUnreadCount(orgId, accessToken ?? ''),
    enabled: orgId !== '' && enabled && !!accessToken,
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}
